/**
 * Language types — Family
 *
 * Single source of truth for the 4 supported UI + translation languages
 * (CLAUDE.md §1, §3, §10). Imported across i18n setup, translate Edge Function,
 * Supabase generated types, and UI components.
 *
 * To add a 5th language in the future (e.g. Arabic for an aunt):
 *   1. Add the code here.
 *   2. Add a JSON file `src/messages/<code>.json` (build will fail until present).
 *   3. Add a linguist context file `src/lib/translate/contexts/<code>.md`.
 *   4. If RTL: enable Tailwind `tailwindcss-rtl` plugin and audit bubble alignment
 *      (CLAUDE.md §7 — logical CSS classes already in place).
 */

export const LANGS = ['tr', 'en', 'bs', 'de'] as const;
export type Lang = (typeof LANGS)[number];

export const LANG_LABELS: Record<Lang, string> = {
  tr: 'Türkçe',
  en: 'English',
  bs: 'Bosanski',
  de: 'Deutsch',
};

/**
 * Type guard — narrows `unknown` (e.g. URL params, localStorage) to Lang.
 */
export function isLang(value: unknown): value is Lang {
  return typeof value === 'string' && (LANGS as readonly string[]).includes(value);
}

/**
 * Smart browser-language detection (Oturum 8 — Begüm's UX request).
 *
 * Critical for the very first page load: a Turkish mother opens the invite
 * link from WhatsApp → she sees the Login landing already in Türkçe.
 * A Bosnian uncle with `bs-BA` or `hr-HR` locale → he sees Bosanski.
 *
 * Match priority:
 *   - 'tr' family (tr-TR)              → tr
 *   - 'en' family (en-US, en-GB)       → en
 *   - Balkan Slavs (bs, hr, sr, me)    → bs (linguistically close, mutually intelligible)
 *   - 'de' family (de-DE, de-AT, etc.) → de
 *   - Other 2-letter ISO codes         → en (universal fallback for non-Turkish)
 *   - Unset/unknown                    → tr (most family members are Turkish-speaking)
 */
export function detectBrowserLang(): Lang {
  if (typeof navigator === 'undefined') return 'tr';
  const raw = (navigator.language || '').toLowerCase();
  const primary = raw.split('-')[0] ?? '';
  if (primary === 'tr') return 'tr';
  if (primary === 'en') return 'en';
  if (primary === 'bs' || primary === 'hr' || primary === 'sr' || primary === 'me') return 'bs';
  if (primary === 'de') return 'de';
  if (primary.length === 2) return 'en';
  return 'tr';
}
